import React from 'react';
import styled from 'styled-components';

const PageContainer = styled.div`
  font-family: 'Arial', sans-serif;
  max-width: 210mm;
  margin: 0 auto;
  padding: 20mm;
  background: white;
  
  @media print {
    margin: 0;
    padding: 15mm;
    box-shadow: none;
    background: white;
    color: black;
    font-size: 12pt;
    line-height: 1.4;
    
    @page {
      size: A4;
      margin: 15mm;
    }
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 3px solid #0066cc;
  
  h1 {
    color: #0066cc;
    font-size: 24px;
    margin: 0 0 10px 0;
    font-weight: bold;
  }
  
  .subtitle {
    color: #666;
    font-size: 14px;
    margin: 0;
  }
  
  .date {
    color: #999;
    font-size: 12px;
    margin-top: 10px;
  }
`;

const GroupeSection = styled.div`
  margin-bottom: 40px;
  page-break-inside: avoid;
  
  .groupe-header {
    background: #f8f9fa;
    padding: 15px;
    margin-bottom: 15px;
    border-left: 4px solid #0066cc;
    border-radius: 4px;
    
    h2 {
      color: #0066cc;
      font-size: 18px;
      margin: 0 0 5px 0;
      font-weight: bold;
    }
    
    .groupe-info {
      color: #666;
      font-size: 14px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      
      .count {
        font-weight: bold;
        color: #0066cc;
      }
    }
  }
`;

const AgentsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
  
  th {
    background: #e9ecef;
    color: #495057;
    font-weight: bold;
    padding: 12px 8px;
    text-align: left;
    border: 1px solid #dee2e6;
    font-size: 12px;
  }
  
  td {
    padding: 10px 8px;
    border: 1px solid #dee2e6;
    font-size: 11px;
    vertical-align: top;
  }
  
  tr:nth-child(even) {
    background: #f8f9fa;
  }
  
  .matricule {
    font-weight: bold;
    color: #0066cc;
    width: 80px;
  }
  
  .nom {
    font-weight: bold;
    width: 120px;
  }
  
  .prenoms {
    width: 120px;
  }
  
  .fonction {
    width: 100px;
    font-style: italic;
  }
  
  .statut {
    width: 60px;
    text-align: center;
    
    .actif {
      color: #28a745;
      font-weight: bold;
    }
    
    .inactif {
      color: #dc3545;
      font-weight: bold;
    }
  }
  
  .photo {
    width: 50px;
    text-align: center;
    
    .photo-indicator {
      padding: 2px 6px;
      border-radius: 3px;
      font-size: 10px;
      font-weight: bold;
      
      &.has-photo {
        background: #d4edda;
        color: #155724;
      }
      
      &.no-photo {
        background: #f8d7da;
        color: #721c24;
      }
    }
  }
`;

const EmptyGroup = styled.div`
  padding: 20px;
  text-align: center;
  color: #6c757d;
  font-style: italic;
  background: #f8f9fa;
  border-radius: 4px;
`;

const Summary = styled.div`
  margin-top: 40px;
  padding: 20px;
  background: #e9ecef;
  border-radius: 4px;
  page-break-inside: avoid;
  
  h3 {
    color: #0066cc;
    font-size: 16px;
    margin: 0 0 15px 0;
  }
  
  .summary-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    
    @media print {
      grid-template-columns: 1fr 1fr;
    }
  }
  
  .summary-item {
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    border-bottom: 1px solid #ced4da;
    
    .label {
      font-weight: bold;
    }
    
    .value {
      color: #0066cc;
      font-weight: bold;
    }
  }
`;

const Footer = styled.div`
  margin-top: 40px;
  padding-top: 20px;
  border-top: 1px solid #dee2e6;
  text-align: center;
  color: #6c757d;
  font-size: 11px;
  
  @media print {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 10px;
  }
`;

const ListeAgentsParGroupeImpression = ({ groupes, agents, selectedGroupeId = null, isPreview = false }) => {
  if (!groupes || !agents) return null;

  // Filtrer les groupes si un groupe spécifique est sélectionné
  const groupesFiltres = selectedGroupeId 
    ? groupes.filter(groupe => groupe.id === selectedGroupeId)
    : groupes;

  // Organiser les agents par groupe
  const agentsParGroupe = groupesFiltres.map(groupe => ({
    ...groupe,
    agents: agents.filter(agent => agent.groupeId === groupe.id)
  }));

  // Agents sans groupe (seulement si on affiche tous les groupes)
  const agentsSansGroupe = selectedGroupeId ? [] : agents.filter(agent => !agent.groupeId);

  // Statistiques (ajustées selon la sélection)
  const agentsConcernes = selectedGroupeId 
    ? agents.filter(agent => agent.groupeId === selectedGroupeId)
    : agents;
  
  const totalAgents = agentsConcernes.length;
  const agentsActifs = agentsConcernes.filter(agent => agent.actif).length;
  const agentsInactifs = totalAgents - agentsActifs;
  const agentsAvecPhoto = agentsConcernes.filter(agent => agent.photoPath).length;

  const renderAgentsTable = (agentsListe) => (
    <AgentsTable>
      <thead>
        <tr>
          <th className="matricule">Matricule</th>
          <th className="nom">Nom</th>
          <th className="prenoms">Prénoms</th>
          <th className="fonction">Fonction</th>
          <th className="statut">Statut</th>
          <th className="photo">Photo</th>
        </tr>
      </thead>
      <tbody>
        {agentsListe.map(agent => (
          <tr key={agent.id}>
            <td className="matricule">{agent.matricule || '-'}</td>
            <td className="nom">{agent.nom}</td>
            <td className="prenoms">{agent.prenoms}</td>
            <td className="fonction">{agent.fonction || '-'}</td>
            <td className="statut">
              <span className={agent.actif ? 'actif' : 'inactif'}>
                {agent.actif ? 'ACTIF' : 'INACTIF'}
              </span>
            </td>
            <td className="photo">
              <span className={`photo-indicator ${agent.photoPath ? 'has-photo' : 'no-photo'}`}>
                {agent.photoPath ? 'OUI' : 'NON'}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </AgentsTable>
  );

  return (
    <PageContainer>
      {isPreview && (
        <div style={{ 
          position: 'absolute', 
          top: '10px', 
          left: '10px', 
          zIndex: 100,
          background: '#28a745',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '4px',
          fontSize: '12px',
          opacity: 0.9
        }}>
          📄 Mode Aperçu
        </div>
      )}

      <Header>
        <h1>
          {selectedGroupeId 
            ? `LISTE DES AGENTS - ${groupesFiltres[0]?.nom?.toUpperCase() || 'GROUPE'}`
            : 'LISTE DES AGENTS PAR GROUPE'
          }
        </h1>
        <p className="subtitle">Système de Gestion des Cartes de Pointage</p>
        <p className="date">Généré le {new Date().toLocaleDateString('fr-FR', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })}</p>
      </Header>

      {/* Groupes avec agents */}
      {agentsParGroupe.map(groupe => (
        <GroupeSection key={groupe.id}>
          <div className="groupe-header">
            <h2>{groupe.nom}</h2>
            <div className="groupe-info">
              <span>{groupe.description || 'Aucune description'}</span>
              <span className="count">
                {groupe.agents.length} agent{groupe.agents.length > 1 ? 's' : ''}
              </span>
            </div>
          </div>
          
          {groupe.agents.length > 0 ? (
            renderAgentsTable(groupe.agents)
          ) : (
            <EmptyGroup>
              Aucun agent assigné à ce groupe
            </EmptyGroup>
          )}
        </GroupeSection>
      ))}

      {/* Agents sans groupe */}
      {agentsSansGroupe.length > 0 && (
        <GroupeSection>
          <div className="groupe-header">
            <h2>AGENTS SANS GROUPE</h2>
            <div className="groupe-info">
              <span>Agents non assignés à un groupe</span>
              <span className="count">
                {agentsSansGroupe.length} agent{agentsSansGroupe.length > 1 ? 's' : ''}
              </span>
            </div>
          </div>
          {renderAgentsTable(agentsSansGroupe)}
        </GroupeSection>
      )}

      {/* Résumé statistique */}
      <Summary>
        <h3>RÉSUMÉ STATISTIQUE</h3>
        <div className="summary-grid">
          <div>
            <div className="summary-item">
              <span className="label">Total Agents:</span>
              <span className="value">{totalAgents}</span>
            </div>
            <div className="summary-item">
              <span className="label">Agents Actifs:</span>
              <span className="value">{agentsActifs}</span>
            </div>
            <div className="summary-item">
              <span className="label">Agents Inactifs:</span>
              <span className="value">{agentsInactifs}</span>
            </div>
          </div>
          <div>
            <div className="summary-item">
              <span className="label">Groupes Configurés:</span>
              <span className="value">{groupes.length}</span>
            </div>
            <div className="summary-item">
              <span className="label">Agents avec Photo:</span>
              <span className="value">{agentsAvecPhoto}</span>
            </div>
            <div className="summary-item">
              <span className="label">Agents sans Photo:</span>
              <span className="value">{totalAgents - agentsAvecPhoto}</span>
            </div>
          </div>
        </div>
      </Summary>

      <Footer>
        <div>Document généré automatiquement par le Système de Gestion des Cartes de Pointage</div>
        <div style={{ marginTop: '5px' }}>
          © {new Date().getFullYear()} - Tous droits réservés
        </div>
      </Footer>
    </PageContainer>
  );
};

export default ListeAgentsParGroupeImpression;
